import React from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { reportToRollbar } from "@/lib/rollbarReport";

export function GlobalErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 sm:p-12 animate-fade-in text-center">
      <div className="max-w-md w-full p-8 rounded-2xl border border-border/40 bg-card shadow-lg flex flex-col items-center">
        <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-6">
          <AlertTriangle className="h-8 w-8 text-destructive opacity-80" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight mb-2">Something went wrong</h1>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          We hit an unexpected error. Your data is safe — try reloading the page.
        </p>
        {(import.meta.env.MODE === "development" || (window as unknown as { DEBUG_ERROR?: boolean }).DEBUG_ERROR) && (
          <pre className="text-left w-full max-h-32 overflow-auto bg-muted/50 p-3 rounded-lg text-[10px] text-destructive mb-6 font-mono whitespace-pre-wrap select-all">
            {error instanceof Error ? error.message : String(error)}
          </pre>
        )}
        <Button
          onClick={resetErrorBoundary}
          size="lg"
          className="w-full gap-2 font-semibold tracking-wide"
        >
          <RotateCcw className="h-4 w-4" />
          Reload page
        </Button>
      </div>
    </div>
  );
}

export function GlobalErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      FallbackComponent={GlobalErrorFallback}
      onError={(error, info) => {
        reportToRollbar(error, {
          tags:  { boundary: "global" },
          extra: { componentStack: info.componentStack },
        });
      }}
      onReset={() => {
        window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
